import React, { useState } from 'react';
import { Facebook, ImageOff, Instagram } from 'lucide-react';
import { ctaLabel } from '@/lib/campaign';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdImage({ src }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="preview-no-image">
        <ImageOff className="w-6 h-6" />
        <span className="text-[10px]">Image couldn't be made yet</span>
      </div>
    );
  }
  return <img src={src} alt="Ad" className="w-full h-full object-cover" onError={() => setBroken(true)} />;
}

export default function PhonePreview({ variant, pageName = 'Your Business' }) {
  const [platform, setPlatform] = useState('facebook');

  if (!variant) return null;

  const imageUrl = variant.image_url
    ? (variant.image_url.startsWith('http') ? variant.image_url : `${BACKEND_URL}${variant.image_url}`)
    : '';
  const hasButton = variant.cta && variant.cta !== 'NO_BUTTON';  // engagement ads are plain photo posts
  const handle = pageName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'your_business';

  return (
    <div className="flex flex-col items-center gap-3" data-testid="phone-preview">
      {/* Platform toggle */}
      <div className="flex gap-1 p-1 rounded-xl bg-card border-2 border-ink">
        <button onClick={() => setPlatform('facebook')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            platform === 'facebook' ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400' : 'text-muted-foreground hover:text-foreground'
          }`} data-testid="preview-fb-toggle">
          <Facebook className="w-3.5 h-3.5" /> Facebook
        </button>
        <button onClick={() => setPlatform('instagram')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            platform === 'instagram' ? 'bg-blush/60 text-rose-700 dark:text-rose-400' : 'text-muted-foreground hover:text-foreground'
          }`} data-testid="preview-ig-toggle">
          <Instagram className="w-3.5 h-3.5" /> Instagram
        </button>
      </div>

      {/* Phone frame */}
      <div className="phone-frame">
        <div className="h-full overflow-y-auto">
          {/* Status bar */}
          <div className="h-11 bg-zinc-900 flex items-end justify-between px-5 pb-1">
            <span className="text-[10px] text-muted-foreground font-medium">9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-3.5 h-2 border border-zinc-400 rounded-sm"><div className="w-2/3 h-full bg-zinc-400 rounded-sm" /></div>
            </div>
          </div>

          {platform === 'facebook' ? (
            <div className="bg-zinc-900">
              {/* FB header */}
              <div className="px-3 py-2 flex items-center gap-2 border-b border-zinc-800">
                <div className="w-8 h-8 rounded-full bg-brand border-2 border-[#141414] flex items-center justify-center text-[10px] font-bold text-[#141414]">Ad</div>
                <div>
                  <p className="text-[11px] font-semibold text-zinc-100">{pageName}</p>
                  <p className="text-[9px] text-muted-foreground">Sponsored · <span className="text-muted-foreground">🌐</span></p>
                </div>
              </div>
              {/* Primary text */}
              <div className="px-3 py-2">
                <p className="text-[11px] text-zinc-200 leading-relaxed">{variant.primary_text}</p>
              </div>
              {/* Image */}
              <div className="w-full aspect-square bg-zinc-800 overflow-hidden">
                <AdImage src={imageUrl} />
              </div>
              {/* Headline & CTA */}
              {hasButton && <div className="px-3 py-2.5 bg-zinc-850 border-t border-zinc-800">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">{variant.description}</p>
                    <p className="text-[11px] font-semibold text-zinc-100 truncate">{variant.headline}</p>
                  </div>
                  <div className="shrink-0 px-3 py-1.5 rounded bg-zinc-700 text-[10px] font-semibold text-zinc-100">
                    {ctaLabel(variant.cta)}
                  </div>
                </div>
              </div>}
              {/* Reactions bar */}
              <div className="px-3 py-2 flex items-center justify-between text-[10px] text-muted-foreground border-t border-zinc-800">
                <span>👍 Like</span>
                <span>💬 Comment</span>
                <span>↗ Share</span>
              </div>
            </div>
          ) : (
            <div className="bg-black">
              {/* IG header */}
              <div className="px-3 py-2 flex items-center gap-2 border-b border-zinc-800">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-yellow-500 via-pink-500 to-violet-600 p-[2px]">
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-[8px] font-bold text-white">Ad</div>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-zinc-100">{handle}</p>
                  <p className="text-[9px] text-muted-foreground">Sponsored</p>
                </div>
              </div>
              {/* Image */}
              <div className="w-full aspect-square bg-zinc-900 overflow-hidden">
                <AdImage src={imageUrl} />
              </div>
              {/* CTA button */}
              {hasButton && <div className="px-3 py-2 border-b border-zinc-800">
                <div className="w-full py-1.5 rounded bg-zinc-800 text-center text-[10px] font-semibold text-zinc-200">
                  {ctaLabel(variant.cta)}
                </div>
              </div>}
              {/* Text */}
              <div className="px-3 py-2">
                <p className="text-[11px] text-zinc-100"><span className="font-semibold">{handle}</span> {variant.primary_text}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{variant.headline}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
